import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { recordVitals, type ReadingCode, type TodayReadings } from "@/lib/api";
import { VITAL_FIELDS, bmi, fmtNum, fmtTime, parseBp, rangeOf } from "@/lib/vitals";
import { celsiusToFahrenheit } from "@/lib/vitalLimits";

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    patientId: string;
    patientName: string;
    /** Doctor the patient is queued for, if any — the visit is assigned to them */
    doctorId?: string | null;
    visitId?: string | null;
    /** Today's readings so far, shown next to each field */
    today?: TodayReadings | null;
    onSaved?: (today: TodayReadings) => void;
}

type Values = Record<(typeof VITAL_FIELDS)[number]["key"], string>;
const EMPTY: Values = { bp: "", pulse: "", temp: "", spo2: "", weight: "", height: "", rbs: "" };

/** Local time "HH:MM" for the time-taken field */
function nowHm() {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function RecordVitalsDialog({ open, onOpenChange, patientId, patientName, doctorId, visitId, today, onSaved }: Props) {
    const [values, setValues] = useState<Values>(EMPTY);
    const [takenAt, setTakenAt] = useState(nowHm());
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState<Partial<Record<keyof Values, boolean>>>({});
    // A temperature typed in °C (38.5), kept to show under the °F it became
    const [tempCelsius, setTempCelsius] = useState<string | null>(null);

    useEffect(() => {
        if (open) { setValues(EMPTY); setTakenAt(nowHm()); setTouched({}); setTempCelsius(null); }
    }, [open]);

    // °C to °F, as the prescription pad does — on leaving the field, and on saving straight from it
    const inFahrenheit = (v: Values): Values => {
        const f = celsiusToFahrenheit(v.temp.trim());
        if (!f) return v;
        setTempCelsius(v.temp.trim());
        return { ...v, temp: f };
    };

    const types = today?.types;

    // Per-field problem, or null when empty/valid
    const validate = (v: Values) => {
        const e: Partial<Record<keyof Values, string>> = {};
        for (const f of VITAL_FIELDS) {
            const raw = v[f.key].trim();
            if (!raw) continue;
            if (f.key === "bp") {
                const bp = parseBp(raw);
                if (!bp) { e.bp = "Enter as systolic/diastolic, e.g. 120/80"; continue; }
                const [sLo, sHi] = rangeOf("bp_systolic", types);
                const [dLo, dHi] = rangeOf("bp_diastolic", types);
                if (bp.sys < sLo || bp.sys > sHi || bp.dia < dLo || bp.dia > dHi) e.bp = "Check this value";
                else if (bp.dia >= bp.sys) e.bp = "Diastolic should be lower than systolic";
                continue;
            }
            const n = Number(raw);
            const [lo, hi] = rangeOf(f.key as ReadingCode, types);
            if (!Number.isFinite(n)) e[f.key] = "Numbers only";
            else if (n < lo || n > hi) e[f.key] = `Possible range ${lo}–${hi} ${f.unit}`;
        }
        return e;
    };
    const errors = useMemo(() => validate(values), [values, types]); // eslint-disable-line react-hooks/exhaustive-deps

    const filled = VITAL_FIELDS.some(f => values[f.key].trim() !== "");
    // Today's values only: a months-old height would give a wrong BMI for a growing child
    const computedBmi = bmi(Number(values.weight) || today?.today.weight?.value, Number(values.height) || today?.today.height?.value);

    const save = async () => {
        setTouched(Object.fromEntries(VITAL_FIELDS.map(f => [f.key, true])));
        const current = inFahrenheit(values);
        if (current !== values) setValues(current);
        if (!filled || Object.keys(validate(current)).length > 0) return;
        const readings: Partial<Record<ReadingCode, number>> = {};
        const bp = parseBp(current.bp);
        if (bp) { readings.bp_systolic = bp.sys; readings.bp_diastolic = bp.dia; }
        for (const f of VITAL_FIELDS) {
            if (f.key !== "bp" && current[f.key].trim() !== "") readings[f.key as ReadingCode] = Number(current[f.key]);
        }
        // Time taken today; a time later than now means the clock was left at a future minute — use now
        const [h, m] = takenAt.split(":").map(Number);
        const at = new Date(); at.setHours(h || 0, m || 0, 0, 0);
        const effectiveAt = at.getTime() > Date.now() ? new Date() : at;

        setSaving(true);
        try {
            const res = await recordVitals({ patientId, doctorId: doctorId ?? null, visitId: visitId ?? null, readings, effectiveAt: effectiveAt.toISOString() });
            toast({ title: "Vitals recorded", description: patientName });
            onSaved?.(res.today);
            onOpenChange(false);
        } catch (err: any) {
            toast({ title: "Vitals not saved", description: err?.message ?? "Please try again", variant: "destructive" });
        } finally {
            setSaving(false);
        }
    };

    const todayHint = (key: keyof Values) => {
        const t = today?.today;
        if (!t) return null;
        if (key === "bp") {
            return t.bp_systolic && t.bp_diastolic
                ? `${fmtNum(t.bp_systolic.value)}/${fmtNum(t.bp_diastolic.value)} at ${fmtTime(t.bp_systolic.effectiveAt)}`
                : null;
        }
        const r = t[key as ReadingCode];
        return r ? `${fmtNum(r.value)} at ${fmtTime(r.effectiveAt)}` : null;
    };

    return (
        <Dialog open={open} onOpenChange={o => !saving && onOpenChange(o)}>
            <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]">
                <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
                    <DialogTitle className="text-base">Record vitals</DialogTitle>
                    <DialogDescription className="text-xs">
                        {patientName} · fill in what was measured; blank fields are skipped
                    </DialogDescription>
                </DialogHeader>

                <form
                    className="px-5 py-4 space-y-4 flex-1 min-h-0 overflow-y-auto"
                    onSubmit={e => { e.preventDefault(); save(); }}
                >
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        {VITAL_FIELDS.map(f => {
                            const err = touched[f.key] ? errors[f.key] : undefined;
                            const hint = todayHint(f.key);
                            return (
                                <label key={f.key} className={cn("block space-y-1", f.key === "bp" && "col-span-2 sm:col-span-1")}>
                                    <span className="flex items-baseline justify-between gap-2">
                                        <span className="text-xs font-medium text-foreground">{f.label}</span>
                                        <span className="text-2xs text-muted-foreground">{f.unit}</span>
                                    </span>
                                    <input
                                        id={`vitals-${f.key}`}
                                        inputMode={f.key === "bp" ? "text" : "decimal"}
                                        autoComplete="off"
                                        autoFocus={f.key === "bp"}
                                        placeholder={f.placeholder}
                                        value={values[f.key]}
                                        onChange={e => {
                                            setValues(v => ({ ...v, [f.key]: e.target.value }));
                                            if (f.key === "temp") setTempCelsius(null);
                                        }}
                                        onBlur={() => {
                                            if (f.key === "temp") setValues(inFahrenheit);
                                            setTouched(t => ({ ...t, [f.key]: true }));
                                        }}
                                        aria-invalid={!!err}
                                        className={cn(
                                            "w-full h-9 rounded-md border bg-background px-3 text-sm tabular-nums outline-none transition-colors",
                                            "focus:border-primary focus:ring-2 focus:ring-primary/15",
                                            err ? "border-destructive" : "border-field",
                                        )}
                                    />
                                    {err
                                        ? <span className="block text-2xs text-destructive">{err}</span>
                                        : f.key === "temp" && tempCelsius
                                        ? <span className="block text-2xs text-muted-foreground">Entered {tempCelsius} °C</span>
                                        : hint && <span className="block text-2xs text-muted-foreground">Today: {hint}</span>}
                                </label>
                            );
                        })}
                        <label className="block space-y-1">
                            <span className="text-xs font-medium text-foreground">Time taken</span>
                            <input
                                id="vitals-taken-at"
                                type="time"
                                value={takenAt}
                                onChange={e => setTakenAt(e.target.value)}
                                className="w-full h-9 rounded-md border border-field bg-background px-3 text-sm tabular-nums outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                            />
                        </label>
                    </div>

                    {computedBmi != null && (
                        <p className="text-xs text-muted-foreground">BMI {computedBmi}</p>
                    )}

                    <div data-kb-footer className="flex items-center justify-end gap-2 pt-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                        <Button type="submit" size="sm" disabled={saving || !filled} className="min-w-24">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save vitals"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
